import { FormEvent, useState } from "react";

import Button from "components/Button";
import InputFieldLabel from "components/InputFieldComponents/InputFieldLabel";
import InputFieldWithValidation from "components/InputFieldWithValidation";
import Iso3166CountryCodes from "misc/Iso3166CountryCodes";
import { BillingAddress } from "v2/pages/billing/types";

import BillingComboBox from "./BillingComboBox";


interface CountryOption {
  name: string;
  countryCode: string;
}

const COUNTRY_OPTIONS: CountryOption[] = Object.values(Iso3166CountryCodes);

export const INPUT_CLASS = "px-3 py-2 border !border-neutral-900 !rounded bg-white !text-neutral-900";

interface BillingAddressFormProps {
  defaultValues?: BillingAddress;
  fieldErrors?: Partial<Record<keyof BillingAddress, string>>;
  onFieldChange?: (fieldName: keyof BillingAddress) => void;
  onSubmit: (data: BillingAddress) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
}

export default function BillingAddressForm({
  defaultValues,
  fieldErrors,
  onFieldChange,
  onSubmit,
  onCancel,
  isSubmitting,
}: BillingAddressFormProps) {
  const [selectedCountry, setSelectedCountry] = useState<CountryOption | undefined>(
    defaultValues?.country
      ? COUNTRY_OPTIONS.find((c) => c.countryCode === defaultValues.country)
      : undefined
  );

  const handleCountryChange = (country: CountryOption) => {
    setSelectedCountry(country);
    onFieldChange?.("country");
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    onSubmit({
      firstName: (formData.get("firstName") as string)?.trim() || "",
      lastName: (formData.get("lastName") as string)?.trim() || "",
      company: (formData.get("company") as string)?.trim() || "",
      vatNumber: (formData.get("vatNumber") as string)?.trim() || "",
      address: (formData.get("address") as string)?.trim() || "",
      city: (formData.get("city") as string)?.trim() || "",
      state: (formData.get("state") as string)?.trim() || "",
      zip: (formData.get("zip") as string)?.trim() || "",
      country: selectedCountry?.countryCode || "",
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InputFieldWithValidation
          name="firstName"
          label={ <InputFieldLabel label="First Name" required /> }
          placeholder="John"
          required
          requiredErrorMessage="First name is required"
          value={defaultValues?.firstName}
          errorMessage={fieldErrors?.firstName}
          isValidated={fieldErrors?.firstName !== undefined}
          onChange={() => onFieldChange?.("firstName")}
          className={INPUT_CLASS}
        />
        <InputFieldWithValidation
          name="lastName"
          label={ <InputFieldLabel label="Last Name" required /> }
          placeholder="Doe"
          required
          requiredErrorMessage="Last name is required"
          value={defaultValues?.lastName}
          errorMessage={fieldErrors?.lastName}
          isValidated={fieldErrors?.lastName !== undefined}
          onChange={() => onFieldChange?.("lastName")}
          className={INPUT_CLASS}
        />
      </div>

      <InputFieldWithValidation
        name="address"
        label={ <InputFieldLabel label="Address" required /> }
        placeholder="123 Main Street"
        required
        requiredErrorMessage="Address is required"
        value={defaultValues?.address}
        errorMessage={fieldErrors?.address}
        isValidated={fieldErrors?.address !== undefined}
        onChange={() => onFieldChange?.("address")}
        className={INPUT_CLASS}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InputFieldWithValidation
          name="city"
          label={ <InputFieldLabel label="City" required /> }
          placeholder="San Francisco"
          required
          requiredErrorMessage="City is required"
          value={defaultValues?.city}
          errorMessage={fieldErrors?.city}
          isValidated={fieldErrors?.city !== undefined}
          onChange={() => onFieldChange?.("city")}
          className={INPUT_CLASS}
        />
        <InputFieldWithValidation
          name="zip"
          label={ <InputFieldLabel label="Zip Code" required /> }
          placeholder="94102"
          required
          requiredErrorMessage="Zip code is required"
          value={defaultValues?.zip}
          errorMessage={fieldErrors?.zip}
          isValidated={fieldErrors?.zip !== undefined}
          onChange={() => onFieldChange?.("zip")}
          className={INPUT_CLASS}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InputFieldWithValidation
          name="state"
          label={ <InputFieldLabel label="State" required /> }
          placeholder="CA"
          required
          requiredErrorMessage="State is required"
          value={defaultValues?.state}
          errorMessage={fieldErrors?.state}
          isValidated={fieldErrors?.state !== undefined}
          onChange={() => onFieldChange?.("state")}
          className={INPUT_CLASS}
        />
        <BillingComboBox
          name="country"
          label="Country"
          required
          requiredErrorMessage="Please select a country"
          errorMessage={fieldErrors?.country}
          placeholder="Search for a country"
          options={COUNTRY_OPTIONS}
          inputDisplayValue={(option: CountryOption) => option?.name || ""}
          listValue={(option: CountryOption) => option?.name || ""}
          value={selectedCountry}
          onOptionSelect={handleCountryChange}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InputFieldWithValidation
          name="company"
          label={ <InputFieldLabel label="Company" /> }
          placeholder="Acme Inc."
          value={defaultValues?.company}
          errorMessage={fieldErrors?.company}
          isValidated={fieldErrors?.company !== undefined}
          onChange={() => onFieldChange?.("company")}
          className={INPUT_CLASS}
        />
        <InputFieldWithValidation
          name="vatNumber"
          label={ <InputFieldLabel label="VAT Number" /> }
          placeholder="GB123456789"
          value={defaultValues?.vatNumber}
          errorMessage={fieldErrors?.vatNumber}
          isValidated={fieldErrors?.vatNumber !== undefined}
          onChange={() => onFieldChange?.("vatNumber")}
          className={INPUT_CLASS}
        />
      </div>

      <div className="flex gap-3">
        <Button
          text={isSubmitting ? "SAVING..." : "SAVE CHANGES"}
          type="submit"
          className="button button-primary w-fit"
          size="SM"
          disabled={isSubmitting}
        />
        {onCancel && (
          <Button
            text="CANCEL"
            className="button button-secondary w-fit"
            size="SM"
            onClick={onCancel}
            disabled={isSubmitting}
          />
        )}
      </div>
    </form>
  );
}